import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';

const RegistrationManager = () => {
  const [info, setInfo] = useState(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);

  useEffect(() => { api.get('/registration').then((r) => setInfo(r.data)); }, []);

  if (!info) return <p className="text-white/50">Chargement...</p>;

  const save = async () => {
    try {
      await api.put('/registration', {
        title: info.title,
        dates: info.dates,
        requirements: info.requirements,
        fees: info.fees,
        feesTable: info.feesTable || [],
        documents: info.documents || [],
        process: info.process,
      });
      toast.success('Informations mises à jour');
    } catch (e) { toast.error('Erreur'); }
  };

  // Dates
  const updateDate = (i, k, v) => { const d = [...info.dates]; d[i] = { ...d[i], [k]: v }; setInfo({ ...info, dates: d }); };
  const addDate = () => setInfo({ ...info, dates: [...info.dates, { date: '', event: '' }] });
  const removeDate = (i) => setInfo({ ...info, dates: info.dates.filter((_, idx) => idx !== i) });

  // Requirements
  const updateReq = (i, v) => { const r = [...info.requirements]; r[i] = v; setInfo({ ...info, requirements: r }); };
  const addReq = () => setInfo({ ...info, requirements: [...info.requirements, ''] });
  const removeReq = (i) => setInfo({ ...info, requirements: info.requirements.filter((_, idx) => idx !== i) });

  // Fees table
  const feesTable = info.feesTable || [];
  const updateFeeRow = (i, k, v) => { const t = [...feesTable]; t[i] = { ...t[i], [k]: v }; setInfo({ ...info, feesTable: t }); };
  const addFeeRow = () => setInfo({ ...info, feesTable: [...feesTable, { label: '', t1: '', t2: '', t3: '', annual: '' }] });
  const removeFeeRow = (i) => setInfo({ ...info, feesTable: feesTable.filter((_, idx) => idx !== i) });

  // Documents
  const documents = info.documents || [];
  const updateDoc = (i, k, v) => { const d = [...documents]; d[i] = { ...d[i], [k]: v }; setInfo({ ...info, documents: d }); };
  const addDoc = () => setInfo({ ...info, documents: [...documents, { label: '', url: '' }] });
  const removeDoc = (i) => setInfo({ ...info, documents: documents.filter((_, idx) => idx !== i) });

  const uploadDocFile = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateDoc(i, 'url', res.data.url);
      toast.success('Document téléversé');
    } catch (e) { toast.error('Erreur upload'); }
    finally { setUploadingIdx(null); }
  };

  return (
    <div data-testid="registration-manager">
      <h2 className="text-2xl font-bold text-white mb-8">Inscriptions</h2>
      <div className="space-y-8 max-w-4xl">
        <Input label="Titre (ex : Inscriptions 2026-2027)" value={info.title} onChange={(v) => setInfo({ ...info, title: v })} />
        <Input label="Texte d'explication" value={info.process} onChange={(v) => setInfo({ ...info, process: v })} textarea />

        {/* Dates */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Dates importantes</h3>
            <button onClick={addDate} className="flex items-center gap-1 text-fuchsia-400 text-sm"><Plus size={16} /> Ajouter</button>
          </div>
          {info.dates.map((d, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 mb-3 items-end">
              <Input label="Date" value={d.date} onChange={(v) => updateDate(i, 'date', v)} />
              <Input label="Événement" value={d.event} onChange={(v) => updateDate(i, 'event', v)} />
              <button onClick={() => removeDate(i)} className="p-3 text-white/60 hover:text-red-400 self-end"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>

        {/* Fees table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Tableau de tarifs (par fréquence)</h3>
            <button onClick={addFeeRow} className="flex items-center gap-1 text-fuchsia-400 text-sm"><Plus size={16} /> Ajouter une ligne</button>
          </div>
          <p className="text-white/50 text-xs mb-3">Chaque ligne : intitulé (ex : "1 cours / semaine"), tarif T1 (avec licence), T2, T3, Total année.</p>
          {feesTable.map((row, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2 mb-3 items-end">
              <Input label={i === 0 ? 'Fréquence' : ''} value={row.label || ''} onChange={(v) => updateFeeRow(i, 'label', v)} placeholder="1 cours / semaine" />
              <Input label={i === 0 ? 'T1' : ''} value={row.t1 || ''} onChange={(v) => updateFeeRow(i, 't1', v)} placeholder="195€" />
              <Input label={i === 0 ? 'T2' : ''} value={row.t2 || ''} onChange={(v) => updateFeeRow(i, 't2', v)} placeholder="135€" />
              <Input label={i === 0 ? 'T3' : ''} value={row.t3 || ''} onChange={(v) => updateFeeRow(i, 't3', v)} placeholder="135€" />
              <Input label={i === 0 ? 'Total année' : ''} value={row.annual || ''} onChange={(v) => updateFeeRow(i, 'annual', v)} placeholder="465€" />
              <button onClick={() => removeFeeRow(i)} className="p-3 text-white/60 hover:text-red-400"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>

        {/* Legacy fees kept for compatibility */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">Tarifs de référence (affichage libre)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Licence" value={info.fees?.license || ''} onChange={(v) => setInfo({ ...info, fees: { ...info.fees, license: v } })} />
            <Input label="Cotisation annuelle" value={info.fees?.annual || ''} onChange={(v) => setInfo({ ...info, fees: { ...info.fees, annual: v } })} />
            <Input label="Trimestriel" value={info.fees?.trimestriel || ''} onChange={(v) => setInfo({ ...info, fees: { ...info.fees, trimestriel: v } })} />
          </div>
        </div>

        {/* Requirements */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Pièces requises</h3>
            <button onClick={addReq} className="flex items-center gap-1 text-fuchsia-400 text-sm"><Plus size={16} /> Ajouter</button>
          </div>
          {info.requirements.map((r, i) => (
            <div key={i} className="flex gap-3 mb-2 items-center">
              <input value={r} onChange={(e) => updateReq(i, e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:border-fuchsia-500/50 focus:outline-none" />
              <button onClick={() => removeReq(i)} className="p-2 text-white/60 hover:text-red-400"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>

        {/* Documents */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Documents à télécharger</h3>
            <button onClick={addDoc} className="flex items-center gap-1 text-fuchsia-400 text-sm"><Plus size={16} /> Ajouter</button>
          </div>
          <p className="text-white/50 text-xs mb-3">Ex : Questionnaire de santé, Règlement intérieur… Chargez un PDF/image ou collez un lien.</p>
          {documents.map((d, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr_auto_auto] gap-2 mb-3 items-end">
              <Input label={i === 0 ? 'Intitulé' : ''} value={d.label || ''} onChange={(v) => updateDoc(i, 'label', v)} placeholder="Règlement intérieur" />
              <Input label={i === 0 ? 'URL / lien' : ''} value={d.url || ''} onChange={(v) => updateDoc(i, 'url', v)} placeholder="https://..." />
              <label className="cursor-pointer flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:border-fuchsia-500/50 text-sm">
                {uploadingIdx === i ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {uploadingIdx === i ? 'Envoi...' : 'Téléverser'}
                <input type="file" onChange={(e) => uploadDocFile(i, e.target.files[0])} className="hidden" disabled={uploadingIdx === i} />
              </label>
              <button onClick={() => removeDoc(i)} className="p-3 text-white/60 hover:text-red-400"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>

        <button onClick={save} data-testid="save-registration-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>
      </div>
    </div>
  );
};

export default RegistrationManager;
